import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { getActionableError } from "@/lib/errors";
import { scoreToRating, scoreToTone } from "@/lib/scoreGrade";
import Nav from "@/components/Nav";
import PublicDemoNav from "@/components/PublicDemoNav";
import ConversationHistoryTimeline from "@/components/ConversationHistoryTimeline";
import RelationshipBookmark from "@/components/RelationshipBookmark";
import PrepareNextCall from "@/components/PrepareNextCall";
import FollowUpEmail from "@/components/FollowUpEmail";
import { DEMO_RELATIONSHIP_DATA } from "@/data/liveDemoData";
import { toast } from "sonner";
import {
  ArrowClockwise,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Buildings,
  CaretDown,
  ClockCounterClockwise,
  ChatCircle,
  CurrencyCircleDollar,
  Equals,
  Handshake,
  HeartBreak,
  LinkSimple,
  SealCheck,
  UsersThree,
  WarningCircle,
  Flag,
  Phone,
  Lightbulb,
  Plus,
  UserCircle,
  Warning,
} from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function milestoneIsProcessing(milestone) {
  const status = milestone?.processing?.status || milestone?.processing_status || "";
  return ["queued", "transcribing", "analyzing"].includes(String(status).toLowerCase());
}

function milestoneExplicitlyIneligibleForFollowUp(milestone) {
  const classification = milestone?.analysis?.conversation_classification
    || milestone?.conversation_classification
    || {};
  return (
    milestone?.is_customer_conversation === false
    || classification.is_customer_conversation === false
  );
}

function RelationshipFollowUpState({ state }) {
  const config = {
    previous: { label: "Previous call", icon: ClockCounterClockwise },
    analyzing: { label: "Analyzing...", icon: ArrowClockwise },
    unavailable: { label: "Not available", icon: Warning },
  }[state] || { label: "Not available", icon: Warning };
  const Icon = config.icon;

  return (
    <span
      className={`relationship-story-card__follow-up relationship-story-card__follow-up--state is-${state}`}
      aria-label={config.label}
    >
      <Icon size={14} weight="bold" aria-hidden="true" />
      <span>{config.label}</span>
    </span>
  );
}

function normalizeGermanSellerRole(value) {
  if (typeof value !== "string") return value;

  return value
    .replace(/\bDer Commercial\b/g, "Der Vertriebsmitarbeiter")
    .replace(/\bder Commercial\b/g, "der Vertriebsmitarbeiter")
    .replace(/\bDen Commercial\b/g, "Den Vertriebsmitarbeiter")
    .replace(/\bden Commercial\b/g, "den Vertriebsmitarbeiter")
    .replace(/\bDem Commercial\b/g, "Dem Vertriebsmitarbeiter")
    .replace(/\bdem Commercial\b/g, "dem Vertriebsmitarbeiter")
    .replace(/\bEin Commercial\b/g, "Ein Vertriebsmitarbeiter")
    .replace(/\bein Commercial\b/g, "ein Vertriebsmitarbeiter")
    .replace(/\bEinen Commercial\b/g, "Einen Vertriebsmitarbeiter")
    .replace(/\beinen Commercial\b/g, "einen Vertriebsmitarbeiter");
}

function actionText(value) {
  if (typeof value !== "string") return value;
  const cleaned = normalizeGermanSellerRole(value)
    .replace(/^(?:(?:the|il|le|el|der)\s+)?(?:rep|sales rep|sales representative|account manager|salesperson|sales agent|commerciale|commercial|representante comercial|vertriebsmitarbeiter)\s*:\s*/i, "")
    .replace(/^(?:(?:the|il|le|el|der)\s+)?(?:rep|sales rep|sales representative|account manager|salesperson|sales agent|commerciale|commercial|representante comercial|vertriebsmitarbeiter)\s+(?:must|should|will|needs to|committed to|deve|dovrà|dovrebbe|si è impegnato a|doit|devra|s'est engagé à|debe|deberá|se comprometió a|muss|soll|wird)\s+/i, "")
    .trim();
  return cleaned ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : cleaned;
}

function itemText(item) {
  if (item == null) return "";
  if (typeof item === "string") return actionText(item);
  if (typeof item !== "object") return String(item);
  if (item.what) return actionText(item.what);
  if (item.text) return actionText(item.text);
  if (item.name) {
    const role = String(item.role || "").trim();
    const showRole = role && !/^(?:not (?:mentioned|evident|established|available)|none identified|unknown|n\/a|—)$/i.test(role);
    return normalizeGermanSellerRole(`${item.name}${showRole ? ` · ${role}` : ""}`);
  }
  return normalizeGermanSellerRole(item.answer || item.description || "");
}

function meaningfulItemText(item) {
  const text = String(itemText(item) || "").trim();
  if (!text) return "";
  if (/^(?:not (?:mentioned|evident|established|available)|none identified|n\/a|—)$/i.test(text)) {
    return "";
  }
  return text;
}

function memoryTokens(value) {
  const stopWords = new Set([
    "alla", "allo", "anche", "come", "dalla", "delle", "della", "degli", "dello",
    "into", "from", "that", "the", "this", "with", "pour", "dans", "avec", "para", "con",
  ]);
  return new Set(
    itemText(value)
      .toLocaleLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .match(/[a-z0-9]+/g)
      ?.filter((token) => token.length >= 4 && !stopWords.has(token)) || []
  );
}

function memoryOverlap(left, right) {
  const a = memoryTokens(left);
  const b = memoryTokens(right);
  if (!a.size || !b.size) return false;
  let shared = 0;
  a.forEach((token) => {
    if (b.has(token)) shared += 1;
  });
  return shared / Math.min(a.size, b.size) >= 0.68;
}

function uniqueMemoryItems(items, exclude = [], limit = 8) {
  const kept = [];
  (Array.isArray(items) ? items : []).forEach((item) => {
    if (!meaningfulItemText(item)) return;
    if (exclude.some((other) => memoryOverlap(item, other))) return;
    if (kept.some((other) => memoryOverlap(item, other))) return;
    if (kept.length < limit) kept.push(item);
  });
  return kept;
}

function GroupedMemoryCard({ title, groups, tone = "violet" }) {
  const visibleGroups = groups
    .map((group) => ({ ...group, items: uniqueMemoryItems(group.items, [], 6) }))
    .filter((group) => group.items.length);
  if (!visibleGroups.length) return null;
  return (
    <div className={`relationship-memory-group-card relationship-memory-group-card--${tone}`}>
      <div className="relationship-memory-list__title">{title}</div>
      <div className="relationship-memory-group-card__groups">
        {visibleGroups.map((group) => (
          <div key={group.label}>
            <small>{group.label}</small>
            <ul>
              {group.items.map((item, index) => (
                <li key={index}>
                  <span aria-hidden="true">•</span>
                  <span>{itemText(item)}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function memorySourceCallId(item, fallback = null) {
  if (!item || typeof item !== "object") return fallback;
  return item.source_call_id || item.last_seen_call_id || item.call_id || fallback;
}

function LastObservedLink({
  callId,
  sourceMap,
  relationshipKey,
  relationshipName,
  deepDiveTarget,
  label = "Last observed",
}) {
  if (!callId) return null;
  const source = sourceMap?.[String(callId)] || {};

  const deepLinkSearch = deepDiveTarget
    ? `?deepDiveTarget=${encodeURIComponent(deepDiveTarget)}`
    : "";

  const isDemoCall = String(callId).startsWith("demo-");

  return (
    <Link
      to={isDemoCall
        ? { pathname: "/demo/renewals", search: deepDiveTarget ? `?section=analysis&deepDiveTarget=${encodeURIComponent(deepDiveTarget)}` : "?section=analysis" }
        : { pathname: `/calls/${callId}`, search: deepLinkSearch }}
      state={isDemoCall ? undefined : {
        from: "relationship",
        relationshipKey,
        relationshipName,
        ...(deepDiveTarget ? { deepDiveTarget } : {}),
      }}
      className="relationship-source-link"
      title={source.title ? `Open ${source.title}` : "Open supporting call"}
    >
      <span>{label}</span>
      <strong>{source.title || "Supporting call"}</strong>
      {source.date && <small>{fmtDate(source.date)}</small>}
      <ArrowRight size={12} weight="bold" aria-hidden="true" />
    </Link>
  );
}

function MemoryList({
  title,
  items,
  tone = "violet",
  card = false,
  icon: Icon,
  legacyTitle = false,
  sourceMap,
  relationshipKey,
  relationshipName,
  deepDiveTarget,
}) {
  const visible = Array.isArray(items) ? items.filter(meaningfulItemText).slice(0, 6) : [];
  if (!visible.length) return null;

  return (
    <div
      className={`relationship-memory-list relationship-memory-list--${tone}${
        card ? " relationship-memory-list--card" : ""
      }${legacyTitle ? " relationship-memory-list--legacy-title" : ""}`}
    >
      <div className="relationship-memory-list__title">
        {Icon && <Icon size={13} weight="fill" aria-hidden="true" />}
        <span>{title}</span>
      </div>
      <ul>
        {visible.map((item, index) => {
          const callId = memorySourceCallId(item);
          return (
            <li key={index}>
              <span aria-hidden="true">•</span>
              <span className="relationship-memory-list__item-copy">
                <span>{itemText(item)}</span>
                <LastObservedLink
                  callId={callId}
                  sourceMap={sourceMap}
                  relationshipKey={relationshipKey}
                  relationshipName={relationshipName}
                  deepDiveTarget={deepDiveTarget}
                />
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function relationshipScoreTone(value, invert = false) {
  return scoreToTone(value, { invert });
}

function capitalizeFirstLetter(value) {
  return String(value || "").replace(/\p{L}/u, (letter) => letter.toLocaleUpperCase());
}

function StateMetric({
  label,
  value,
  score,
  icon: Icon,
  tone = "default",
  invert = false,
  className = "",
  sourceCallId,
  sourceMap,
  relationshipKey,
  relationshipName,
  deepDiveTarget,
  sourceLabel = "Last observed",
}) {
  const hasScore = score !== undefined && score !== null;
  const emptyValue = label === "Stance" ? "No clear stance detected" : "—";
  const rawShown = value || (hasScore ? scoreToRating(score) : emptyValue);
  const shown = label === "Stance" && !hasScore ? capitalizeFirstLetter(rawShown) : rawShown;
  const metricClass = hasScore
    ? `relationship-state-metric--score relationship-state-metric--score-${relationshipScoreTone(score, invert)}`
    : `relationship-state-metric--${tone}`;

  return (
    <div
      className={`relationship-state-metric ${metricClass} ${className}`.trim()}
    >
      <div className="relationship-state-metric__label">
        {Icon && <Icon size={13} weight="fill" aria-hidden="true" />}
        <span>{label}</span>
      </div>
      <div className={`relationship-state-metric__value${hasScore ? " score-grade" : ""}`}>{shown}</div>
      <LastObservedLink
        callId={sourceCallId}
        sourceMap={sourceMap}
        relationshipKey={relationshipKey}
        relationshipName={relationshipName}
        deepDiveTarget={deepDiveTarget}
        label={sourceLabel}
      />
    </div>
  );
}

const EVOLUTION_STATUS_ALIASES = {
  resolved: "resolved",
  improving: "improving",
  persistent: "persistent",
  critical: "critical",
  recurring: "persistent",
  current: "persistent",
  new: "persistent",
  open: "persistent",
};

function evolutionStep(step) {
  if (typeof step === "string") {
    return { text: normalizeGermanSellerRole(step), status: "persistent" };
  }

  const rawStatus = String(step?.evolution_status || step?.status || "persistent").toLowerCase();
  return {
    text: normalizeGermanSellerRole(step?.text || ""),
    status: EVOLUTION_STATUS_ALIASES[rawStatus] || "persistent",
  };
}

const EVOLUTION_COLOR = {
  resolved: "is-resolved",
  improving: "is-improving",
  persistent: "is-persistent",
  critical: "is-critical",
};

function RelationshipMemory({ memory, onRebuild, rebuilding, milestones, relationshipKey, relationshipName }) {
  if (!memory) return null;

  if (!memory.available || memory.needs_rebuild) {
    return (
      <section id="next-call" className="relationship-memory-empty" data-testid="memory-empty-state">
        <div className="relationship-memory-empty__identity">
          <span className="relationship-memory-empty__icon">
            <ClockCounterClockwise size={22} weight="duotone" />
          </span>
          <div>
            <h2>{memory.needs_rebuild ? "Memory needs a refresh" : "Build the relationship memory"}</h2>
            <p>
              {memory.needs_rebuild
                ? "The memory schema changed. Rebuild the compact state from the stored calls."
                : "Build a compact longitudinal state from the analyses already stored for this customer."}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onRebuild}
          disabled={rebuilding}
          className="relationship-action-button"
          data-testid="build-relationship-memory"
        >
          <ClockCounterClockwise size={15} weight="bold" />
          {rebuilding ? "Building memory..." : memory.needs_rebuild ? "Rebuild memory" : "Build relationship memory"}
        </button>
      </section>
    );
  }

  const state = memory.current_state || {};
  const facts = state.facts || {};
  const inferences = state.inferences || {};
  const brief = memory.next_call_brief || {};

  const commitments = uniqueMemoryItems(
    (facts.commitments || []).filter((item) => item?.status === "open" || item?.status === "unknown"),
  );

  const commercialObjections = uniqueMemoryItems(
    (facts.commercial_objections || facts.explicit_objections || []).filter(
      (item) => !item?.status || item.status === "open",
    ),
  );
  const blockers = uniqueMemoryItems(
    (facts.operational_blockers || []).filter((item) => !item?.status || item.status === "open"),
    commercialObjections,
  );
  const preferences = uniqueMemoryItems(
    (facts.process_preferences || []).filter((item) => !item?.status || item.status === "active"),
    [...commercialObjections, ...blockers],
  );
  const riskSource = Array.isArray(brief.risks) && brief.risks.length
    ? brief.risks
    : inferences.risks;
  const opportunitySource = Array.isArray(brief.opportunities) && brief.opportunities.length
    ? brief.opportunities
    : inferences.opportunities;

  const risks = uniqueMemoryItems(
    riskSource,
    [...commercialObjections, ...blockers, ...preferences, ...commitments],
    6,
  );
  const opportunities = uniqueMemoryItems(
    opportunitySource,
    [...commercialObjections, ...blockers, ...preferences, ...risks, ...commitments],
    6,
  );

  const sourceMap = Object.fromEntries(
    (Array.isArray(milestones) ? milestones : []).map((milestone) => [
      String(milestone.call_id),
      milestone,
    ])
  );
  const statusSourceCallId = state.as_of_call_id || memory.last_processed_call_id;
  const hasWatchItems = Boolean(
    commercialObjections.length || blockers.length || risks.length || opportunities.length
  );
  const stakeholders = uniqueMemoryItems(facts.stakeholders, [], 6).filter((item) => (
    !/^(?:non nominato|unnamed|unknown)\b/i.test(itemText(item))
  ));

  return (
    <section className="relationship-memory relationship-memory--final" data-testid="relationship-memory">
      <article id="relationship-state" className="relationship-state relationship-state--final">
        <div className="relationship-card-header">
          <div><h2>Current relationship state</h2></div>
        </div>

        <div className="relationship-state__section relationship-state__section--snapshot">
          <div className="relationship-state__section-title">Snapshot</div>
          <div className="relationship-state__snapshot relationship-state__snapshot--final">
            <div className="relationship-state__snapshot-copy relationship-state__snapshot-copy--two">
              <MemoryList
                title="Stakeholders"
                icon={UsersThree}
                items={stakeholders}
                tone="violet"
                card
                sourceMap={sourceMap}
                relationshipKey={relationshipKey}
                relationshipName={relationshipName}
              />
              <StateMetric
                label="Status Quo"
                icon={Equals}
                value={facts.customer_position}
                className="relationship-state-metric--summary"
                sourceCallId={statusSourceCallId}
                sourceMap={sourceMap}
                relationshipKey={relationshipKey}
                relationshipName={relationshipName}
                sourceLabel="Last updated"
              />
            </div>

            <div className="relationship-state__score-grid relationship-state__score-grid--three" aria-label="Commercial signals">
              <StateMetric label="Buying intent" icon={CurrencyCircleDollar} score={inferences.buying_intent?.score} />
              <StateMetric label="Churn risk" icon={HeartBreak} score={inferences.churn_risk?.score} invert />
              <StateMetric label="Trust" icon={SealCheck} score={inferences.trust?.score} />
            </div>
          </div>
        </div>

        <div className="relationship-state__details relationship-state__details--final">
          {hasWatchItems && (
            <div className="relationship-state__section">
              <div className="relationship-state__section-title">What to watch</div>
              <div className="relationship-state__watch-grid">
                <MemoryList
                  title="Blockers"
                  icon={WarningCircle}
                  items={blockers}
                  tone="violet"
                  card
                  sourceMap={sourceMap}
                  relationshipKey={relationshipKey}
                  relationshipName={relationshipName}
                  deepDiveTarget="blockers"
                />
                <MemoryList
                  title="Objections"
                  icon={Flag}
                  items={commercialObjections}
                  tone="violet"
                  card
                  sourceMap={sourceMap}
                  relationshipKey={relationshipKey}
                  relationshipName={relationshipName}
                  deepDiveTarget="objections"
                />
                <MemoryList
                  title="Risks"
                  icon={Warning}
                  items={risks}
                  tone="violet"
                  card
                  sourceMap={sourceMap}
                  relationshipKey={relationshipKey}
                  relationshipName={relationshipName}
                  deepDiveTarget="risks"
                />
                <MemoryList
                  title="Opportunities"
                  icon={Lightbulb}
                  items={opportunities}
                  tone="violet"
                  card
                  sourceMap={sourceMap}
                  relationshipKey={relationshipKey}
                  relationshipName={relationshipName}
                  deepDiveTarget="opportunities"
                />
              </div>
            </div>
          )}

          <div className="relationship-state__section">
            <div className="relationship-state__section-title">Human dynamics</div>
            <div className="relationship-state__health-grid">
              <StateMetric label="Relationship score" icon={LinkSimple} score={inferences.relationship_strength?.score} />
              <StateMetric
                label="Stance"
                icon={Handshake}
                value={inferences.attitude?.text}
                sourceCallId={memorySourceCallId(inferences.attitude, statusSourceCallId)}
                sourceMap={sourceMap}
                relationshipKey={relationshipKey}
                relationshipName={relationshipName}
                deepDiveTarget="stance"
              />
              <StateMetric
                label="Tone"
                icon={ChatCircle}
                value={inferences.emotional_tone?.text}
                sourceCallId={memorySourceCallId(inferences.emotional_tone, statusSourceCallId)}
                sourceMap={sourceMap}
                relationshipKey={relationshipKey}
                relationshipName={relationshipName}
                deepDiveTarget="tone"
              />
            </div>
          </div>
        </div>
      </article>
    </section>
  );
}



function uploadPathForRelationship(customer, fallbackKey) {
  const params = new URLSearchParams();
  const relationshipId = customer?.id || fallbackKey;
  const customerName = String(customer?.name || "").trim();
  if (relationshipId) params.set("relationshipId", relationshipId);
  if (customerName) params.set("customerName", customerName);
  const query = params.toString();
  return query ? `/upload?${query}` : "/upload";
}

export default function CustomerTimelinePage({ demoMode = false }) {
  const { key: routeKey } = useParams();
  const key = demoMode ? DEMO_RELATIONSHIP_DATA.customer.key : routeKey;
  const demoStage = demoMode && typeof window !== "undefined"
    ? Number(window.sessionStorage.getItem("callsect:live-demo-stage") || 0)
    : 0;
  const [data, setData] = useState(() => demoMode ? DEMO_RELATIONSHIP_DATA : null);
  const [loading, setLoading] = useState(!demoMode);
  const [loadError, setLoadError] = useState("");
  const [rebuilding, setRebuilding] = useState(false);
  const [showAllCalls, setShowAllCalls] = useState(false);
  const nav = useNavigate();
  const location = useLocation();
  const highlightedCallRef = useRef(false);
  const activeCallId = location.state?.activeCallId;

  const loadTimeline = async () => {
    if (demoMode) {
      setData(DEMO_RELATIONSHIP_DATA);
      setLoading(false);
      setLoadError("");
      return;
    }
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await api.get(`/customers/${encodeURIComponent(key)}/timeline`);
      setData(data);
    } catch (error) {
      const message = getActionableError(error, "Callsect could not load this relationship timeline.");
      setLoadError(message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTimeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoMode, key]);

  useEffect(() => {
    if (!data || !activeCallId) return;
    const sortedMilestones = [...(data.milestones || [])].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    const activeIndex = sortedMilestones.findIndex(
      (milestone) => String(milestone.call_id) === String(activeCallId)
    );
    if (activeIndex >= 3) setShowAllCalls(true);
  }, [activeCallId, data]);

  useEffect(() => {
    if (!data || !activeCallId || highlightedCallRef.current) return;
    const frame = window.requestAnimationFrame(() => {
      const target = document.getElementById(`timeline-call-${activeCallId}`);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
        highlightedCallRef.current = true;
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeCallId, data, showAllCalls]);

  const rebuildMemory = async () => {
    if (demoMode) {
      toast.success("Synthetic relationship memory is already up to date");
      return;
    }
    const relationshipId = data?.customer?.id || key;
    setRebuilding(true);

    try {
      const result = await api.post(`/customers/${encodeURIComponent(relationshipId)}/memory/rebuild`);
      const refreshed = await api.get(`/customers/${encodeURIComponent(relationshipId)}/timeline`);
      setData(refreshed.data);
      toast.success(`Relationship memory built from ${result.data.processed_calls} calls`);
    } catch (err) {
      toast.error(getActionableError(err, "Relationship memory could not be rebuilt."));
    } finally {
      setRebuilding(false);
    }
  };

  if (demoMode && demoStage < 3) {
    return (
      <div className={`relationship-page${demoMode ? " relationship-page--demo" : " relationship-page--authenticated"}`}>
        <PublicDemoNav demoStage={demoStage} />
        <main className="relationship-shell product-page-enter">
          <div className="relationship-not-found relationship-not-found--demo-gate">
            <ClockCounterClockwise size={28} weight="duotone" />
            <strong>The relationship and next-call layer is the final demo step.</strong>
            <p>Complete the Dissection, generate the CRM note, and open Coaching before revealing the client memory and personalised next-call preparation.</p>
            <div className="relationship-not-found__actions">
              <button type="button" onClick={() => nav("/demo")}>Return to guided call</button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`relationship-page${demoMode ? " relationship-page--demo" : " relationship-page--authenticated"}`}>
        {demoMode ? <PublicDemoNav demoStage={demoStage} /> : <Nav />}
        <main className="relationship-shell product-page-enter">
          <div className="relationship-page-loading">
            <div className="relationship-page-loading__hero" />
            <div className="relationship-page-loading__panel" />
            <div className="relationship-page-loading__panel" />
          </div>
        </main>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={`relationship-page${demoMode ? " relationship-page--demo" : " relationship-page--authenticated"}`}>
        {demoMode ? <PublicDemoNav demoStage={demoStage} /> : <Nav />}
        <main className="relationship-shell product-page-enter">
          <div className="relationship-not-found">
            {loadError ? <ArrowClockwise size={24} weight="duotone" /> : <Warning size={24} weight="duotone" />}
            <strong>{loadError ? "This relationship could not be loaded" : "Relationship not found"}</strong>
            {loadError && <p>{loadError}</p>}
            <div className="relationship-not-found__actions">
              {loadError && (
                <button type="button" onClick={loadTimeline}>
                  <ArrowClockwise size={15} weight="bold" />
                  Try again
                </button>
              )}
              <button type="button" onClick={() => nav("/customers")}>Back to customers</button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const { customer, milestones, objection_evolution, relationship_memory } = data;
  const newestFirstMilestones = [...milestones].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  const compactMilestones = newestFirstMilestones.slice(0, 3);
  const latestFollowUpMilestone = newestFirstMilestones.find(
    (milestone) => !milestoneExplicitlyIneligibleForFollowUp(milestone)
  );
  const latestFollowUpCallId = String(latestFollowUpMilestone?.call_id || "");
  const historyButtonLabel = showAllCalls
    ? "Compact history"
    : "Expand history";

  return (
    <div className={`relationship-page${demoMode ? " relationship-page--demo" : " relationship-page--authenticated"}`}>
      {demoMode ? <PublicDemoNav demoStage={demoStage} /> : <Nav />}

      <main className="relationship-shell product-page-enter">
        {!demoMode && (
          <div className="relationship-context-topbar">
            <Link to="/customers" className="call-detail-back-link relationship-context-back-link">
              <ArrowLeft size={15} weight="bold" />
              Back to Customers
            </Link>
          </div>
        )}

        {demoMode && (
          <div className="relationship-topbar">
            <Link
              to="/demo/renewals?section=notes"
              className="relationship-upload-button"
            >
              <ArrowRight size={14} weight="bold" />
              Back to guided call
            </Link>
          </div>
        )}

        <section className="relationship-hero">
          <div className="relationship-hero__content">
            <span className="relationship-hero__avatar">
              <UserCircle size={31} weight="duotone" />
            </span>

            <div className="relationship-hero__copy">
              <h1>{customer.name}</h1>
              <div className="relationship-hero__meta">
                {customer.company && (
                  <span>
                    <Buildings size={14} />
                    {customer.company}
                  </span>
                )}
                <span>
                  <Phone size={14} />
                  {customer.call_count} call{customer.call_count === 1 ? "" : "s"}
                </span>
                <span>
                  <ClockCounterClockwise size={14} />
                  {fmtDate(customer.first_call_at)} → {fmtDate(customer.last_call_at)}
                </span>
              </div>
              <RelationshipBookmark
                bookmark={relationship_memory?.current_state?.bookmark}
                className="relationship-hero__bookmark"
              />
            </div>
          </div>

          {!demoMode && (
            <Link
              to={uploadPathForRelationship(customer, key)}
              state={{
                customerName: customer.name,
                relationshipId: customer.id || key,
                from: "relationship",
              }}
              className="relationship-upload-button relationship-upload-button--hero"
            >
              <Plus size={14} weight="bold" />
              Upload another call
            </Link>
          )}
        </section>

        {relationship_memory?.available && !relationship_memory?.needs_rebuild && (
          <PrepareNextCall
            memory={relationship_memory}
            customer={{
              id: customer.id || key,
              name: customer.name,
              call_count: newestFirstMilestones.length,
            }}
            milestones={newestFirstMilestones}
            demoMode={demoMode}
            onMemoryRefresh={rebuildMemory}
            memoryRefreshing={rebuilding}
          />
        )}

        <section
          id="conversation-history"
          className="relationship-history relationship-history--priority"
        >
          <div className="relationship-history__header">
            <div>
              <h2>Conversation history</h2>
            </div>

            <div className="relationship-history__actions">
              <span>
                {newestFirstMilestones.length} call{newestFirstMilestones.length === 1 ? "" : "s"}
              </span>
              {newestFirstMilestones.length > 0 && (
                <button
                  type="button"
                  className="relationship-history__toggle"
                  onClick={() => setShowAllCalls((current) => !current)}
                  aria-expanded={showAllCalls}
                >
                  {historyButtonLabel}
                  <CaretDown size={14} weight="bold" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>

          <ConversationHistoryTimeline
            milestones={showAllCalls ? newestFirstMilestones : compactMilestones}
            objectionEvolution={objection_evolution}
            relationshipCallCount={newestFirstMilestones.length}
            activeCallId={activeCallId}
            allCollapsed={!showAllCalls}
            clickablePreviews={!demoMode}
            getCallLink={(milestone) => ({
              to: `/calls/${milestone.call_id}`,
              state: { from: "relationship", relationshipKey: key, relationshipName: customer.name },
            })}
            renderFollowUpAction={!demoMode ? (milestone) => {
              if (milestoneExplicitlyIneligibleForFollowUp(milestone)) {
                return <RelationshipFollowUpState state="unavailable" />;
              }

              if (String(milestone.call_id) !== latestFollowUpCallId) {
                return <RelationshipFollowUpState state="previous" />;
              }

              if (milestoneIsProcessing(milestone)) {
                return <RelationshipFollowUpState state="analyzing" />;
              }

              if (!milestone.analysis_ready) {
                return <RelationshipFollowUpState state="unavailable" />;
              }

              return (
                <FollowUpEmail
                  callId={milestone.call_id}
                  customerName={customer.name}
                  available={Boolean(milestone.follow_up_email_available)}
                  suggestion={milestone.follow_up_suggestion}
                  triggerClassName="relationship-story-card__follow-up"
                  onSuggestionDismissed={(followUpSuggestion) => setData((current) => current ? ({
                    ...current,
                    milestones: (current.milestones || []).map((item) => (
                      item.call_id === milestone.call_id
                        ? { ...item, follow_up_suggestion: followUpSuggestion }
                        : item
                    )),
                  }) : current)}
                  onSaved={() => setData((current) => current ? ({
                    ...current,
                    milestones: (current.milestones || []).map((item) => (
                      item.call_id === milestone.call_id
                        ? { ...item, follow_up_email_available: true }
                        : item
                    )),
                  }) : current)}
                />
              );
            } : undefined}
            showCurrentBadge={false}
          />

          {demoMode && (
            <aside
              className="relationship-memory-tip relationship-memory-tip--embedded"
              data-testid="relationship-memory-tip"
            >
              <span className="relationship-memory-tip__icon" aria-hidden="true">
                <Lightbulb size={19} weight="duotone" />
              </span>
              <div className="relationship-memory-tip__copy">
                <strong>Back to the guided call</strong>
                <p>Return to the generated note and continue exploring the synthetic demo.</p>
              </div>
              <Link
                to="/demo/renewals?section=notes"
                className="relationship-memory-tip__action"
                aria-label="Return to guided call"
              >
                <ArrowRight size={16} weight="bold" />
              </Link>
            </aside>
          )}
        </section>

        <RelationshipMemory
          memory={relationship_memory}
          onRebuild={rebuildMemory}
          rebuilding={rebuilding}
          milestones={newestFirstMilestones}
          relationshipKey={key}
          relationshipName={customer.name}
        />

        {newestFirstMilestones.length >= 2 && objection_evolution && objection_evolution.length > 0 && (
          <section id="objections" className="objection-evolution" data-testid="evolution-chain">
            <div className="relationship-card-header">
              <div>
                <h2>Objection evolution</h2>
              </div>
            </div>

            <div className="objection-evolution__chain">
              {objection_evolution.map((rawStep, index) => {
                const step = evolutionStep(rawStep);

                return (
                  <div key={index} className="objection-evolution__step">
                    <span
                      data-testid={`evolution-step-${index}`}
                      data-status={step.status}
                      title={`${step.status}: ${step.text}`}
                      className={`objection-evolution__chip ${EVOLUTION_COLOR[step.status] || EVOLUTION_COLOR.persistent}`}
                    >
                      <Flag className="objection-evolution__flag" size={13} weight="fill" />
                      <span className="objection-evolution__text">{step.text}</span>
                    </span>

                    {index < objection_evolution.length - 1 && (
                      <span className="objection-evolution__connector" aria-hidden="true">
                        <span />
                        <ArrowDown size={14} weight="bold" />
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
